import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Truck, MapPin, Mail, Phone, ArrowLeft, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

export default function ShippingPolicyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Nature of Service',
      icon: MapPin,
      content: [
        {
          subtitle: 'We Are A Hospitality Venue',
          text: 'Scratch Unleashed, operated by Charms Hospitality Services, is a physical nightclub and hospitality venue located at 3rd Floor, Bindal Mall, Sonari, Jamshedpur, Jharkhand 831011, India. We do NOT sell physical merchandise online and we do not perform any physical shipping or courier delivery of goods.',
        },
        {
          subtitle: 'What You Are Purchasing',
          text: 'When you complete a booking on scratchjsr.com you are purchasing one of: (a) a table reservation, (b) an event entry pass, (c) a VIP table package, or (d) pre-paid services consumable at the venue on the date of your confirmed booking. All services are rendered in person at the venue.',
        },
      ],
    },
    {
      title: 'Service Delivery / Fulfilment',
      icon: CheckCircle2,
      content: [
        {
          subtitle: 'Confirmation Pass',
          text: 'Immediately after a successful payment, an electronic confirmation (your "entry pass") is delivered to the phone number and / or email associated with the booking via: WhatsApp message, SMS, and/or email. This pass contains your booking ID, QR code, date, entry window, and any package inclusions. The pass is the equivalent of "delivery" for our service.',
        },
        {
          subtitle: 'Delivery Window',
          text: 'The electronic confirmation is dispatched within 60 seconds of successful payment in 99% of cases. In the rare event of a delay, please allow up to 30 minutes. If you have not received your confirmation within 30 minutes, contact us at admin@scratchjsr.com or +91 78703 69666 with your registered phone number and transaction reference.',
        },
        {
          subtitle: 'Service Date',
          text: 'Your service is "delivered" on the date and within the entry window of your confirmed booking, in person at the venue. Doors typically open at 6:00 PM IST on operational nights (Friday, Saturday, Sunday). Specific entry windows for VIP and event bookings are stated in your confirmation.',
        },
      ],
    },
    {
      title: 'No Physical Shipping',
      icon: Truck,
      content: [
        {
          subtitle: 'No Couriers, No Physical Goods',
          text: 'No physical product is shipped to your address. No courier or logistics partner is engaged. There is no shipping cost or delivery charge attached to any booking. Any "shipping address" field that may appear in the payment gateway interface is for billing reconciliation only and is not used by Scratch Unleashed for any physical dispatch.',
        },
        {
          subtitle: 'Physical Items Won At Events',
          text: 'If you win a physical item (e.g., promotional merchandise, gift hamper) during an in-venue event, the item is handed over to you in person at the venue on the night of the event. We do not ship event prizes.',
        },
      ],
    },
    {
      title: 'Did Not Receive Confirmation',
      icon: AlertTriangle,
      content: [
        {
          subtitle: 'Step 1 — Check Spam / WhatsApp',
          text: 'Confirmation emails occasionally land in your spam folder. WhatsApp messages are sent from our verified business number; please check WhatsApp on the phone number used at checkout.',
        },
        {
          subtitle: 'Step 2 — Contact Support',
          text: 'If you still cannot find the confirmation after 30 minutes, email admin@scratchjsr.com or message/call +91 78703 69666 with: your registered phone number, transaction reference (UPI / order ID), date and event of the booking. We will re-send the pass.',
        },
        {
          subtitle: 'Step 3 — Show Up Anyway',
          text: 'Even without the digital pass, our venue staff can look up a confirmed booking using your registered phone number. Carry a valid government photo ID for verification.',
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Shipping &amp; Delivery Policy | Scratch Unleashed</title>
        <meta
          name="description"
          content="Shipping &amp; Service Delivery Policy for Scratch Unleashed (Charms Hospitality Services) — no physical shipping; bookings are delivered electronically and serviced at the venue."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://scratchjsr.com/shipping-policy" />
        <meta property="og:title" content="Shipping &amp; Delivery Policy | Scratch Unleashed" />
        <meta
          property="og:description"
          content="How service delivery works at Scratch Unleashed — electronic confirmation, in-venue fulfilment, no physical shipping."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/shipping-policy" />
      </Helmet>

      <div data-testid="shipping-policy-page" className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="fixed top-6 left-6 z-50">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            data-testid="shipping-back-button"
            className="gap-2 text-[#B9B9B9] hover:text-[#F2F2F2]"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>

        <section className="relative pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
              style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)' }}
            >
              <Truck size={32} style={{ color: '#D4A843' }} />
            </div>
            <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl font-bold text-[#F2F2F2] mb-4">
              Shipping &amp; Delivery Policy
            </h1>
            <p className="text-lg text-[#B9B9B9] max-w-2xl mx-auto">
              We don't ship goods — we host nights. Here's how bookings are confirmed and delivered.
            </p>
            <p className="text-sm text-[#6B6B6B] mt-4">
              <strong>Last Updated:</strong> January 2026
            </p>
          </div>
        </section>

        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {sections.map((section, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                data-testid={`shipping-section-${idx}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <section.icon size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, i) => (
                    <div key={i}>
                      {item.subtitle && (
                        <h3 className="text-lg font-semibold text-[#D4A843] mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-[#B9B9B9] leading-relaxed whitespace-pre-line">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:admin@scratchjsr.com"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#D4A843] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="shipping-email-cta"
              >
                <Mail size={24} className="mx-auto mb-3 text-[#D4A843]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">Email Us</h3>
                <p className="text-sm text-[#B9B9B9]">admin@scratchjsr.com</p>
              </a>
              <a
                href="https://wa.me/917870369666"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#25D366] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="shipping-whatsapp-cta"
              >
                <Phone size={24} className="mx-auto mb-3 text-[#25D366]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">WhatsApp / Phone</h3>
                <p className="text-sm text-[#B9B9B9]">+91 78703 69666</p>
              </a>
            </div>
          </div>
        </section>

        <footer className="border-t border-[rgba(255,255,255,0.07)] py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-[#6B6B6B]">
              © {new Date().getFullYear()} Charms Hospitality Services. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              <button onClick={() => navigate('/terms-and-conditions')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Terms &amp; Conditions</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/privacy-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Privacy Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/refund-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Refund Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Contact Us</button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
